<template>
    <div class="vue-notification" :class="item.type">
        <div class="alert-main">
            <div v-if="item.title" v-dompurify-html="item.title" class="alert-title notification-title" />
            <div v-if="item.text" v-dompurify-html="item.text" class="alert-content notification-content" />
            <div v-if="hasActions" class="alert-actions">
                <slot name="actions" :item="item" :close="close">
                    <b-button
                        v-for="action in actions"
                        :key="action.id"
                        size="sm"
                        :variant="action.variant ?? 'outline-primary'"
                        @click="handleAction(action)">
                        {{ action.label }}
                    </b-button>
                </slot>
            </div>
        </div>
        <button
            type="button"
            class="alert-close"
            :class="{ 'alert-close--no-title': !item.title }"
            :aria-label="t('close')"
            @click="handleClose">
            <i class="fe fe-x" aria-hidden="true" />
        </button>
    </div>
</template>

<script setup lang="ts">
import type { AlertAction, AlertData } from '@/types/alert.types';
import { useAlert } from '@/composables/useAlert';
import { computed, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';

interface AlertItem {
    id?: number;
    title?: string;
    text?: string;
    type?: string;
    data?: AlertData;
}

const props = defineProps<{
    item: AlertItem;
    close: () => void;
}>();

const { t } = useI18n();
const { untrackErrorAlert } = useAlert();
const slots = useSlots();

const actions = computed(() => props.item.data?.actions ?? []);

const hasActions = computed(() => actions.value.length > 0 || !!slots.actions);

function handleClose(): void {
    if (props.item.type === 'error' && props.item.id !== undefined) {
        untrackErrorAlert(props.item.id);
    }
    props.close();
}

function handleAction(action: AlertAction): void {
    action.onClick();
    if (action.closeOnClick !== false) {
        handleClose();
    }
}
</script>

<style scoped lang="scss">
$alert-title-line-height: 1.25;
$alert-title-font-size: 0.875rem;

.vue-notification {
    // Override kyvg default `.vue-notification { display: block }`
    display: flex !important;
    flex-direction: row;
    align-items: flex-start;
    gap: 0.5rem;
    margin-inline-end: 2.25rem !important;
    margin-top: 1rem !important;
    border-radius: 6px;
    padding: 0.625rem;

    border-right-width: 0; // override defaults so they are RTL compatible
    border-inline-start-width: 5px;
    border-inline-start: 5px solid var(--#{$prefix}primary) !important;
    text-align: start !important;

    .alert-main {
        flex: 1;
        min-width: 0;
    }

    .alert-title {
        font-size: $alert-title-font-size;
        line-height: $alert-title-line-height;
        margin-bottom: 0.125rem;

        // Keep title + close on one row when sanitized HTML wraps text in block tags
        :deep(p),
        :deep(div) {
            display: inline;
            margin: 0;
        }
    }

    .alert-content {
        font-size: $font-size-sm;
    }

    .alert-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .alert-close {
        flex-shrink: 0;
        align-self: flex-start;
        border: none;
        background: transparent;
        padding: 0;
        font-size: $alert-title-font-size;
        line-height: 1;
        color: inherit;
        cursor: pointer;
        margin-block-start: calc((#{$alert-title-line-height} - 1) / 2 * #{$alert-title-font-size});

        &--no-title {
            font-size: $font-size-sm;
            margin-block-start: calc((#{$alert-title-line-height} - 1) / 2 * #{$font-size-sm});
        }
    }

    &.success {
        background: var(--#{$prefix}success-bg-subtle);
        color: var(--#{$prefix}success);
        border: 1px solid var(--#{$prefix}success) !important;
    }

    &.warning {
        background: var(--#{$prefix}warning-bg-subtle);
        color: var(--#{$prefix}warning);
        border: 1px solid var(--#{$prefix}warning) !important;
    }

    &.error {
        background: var(--#{$prefix}danger-bg-subtle);
        color: var(--#{$prefix}danger);
        border: 1px solid var(--#{$prefix}danger) !important;
    }

    &.info {
        background: var(--#{$prefix}info-bg-subtle);
        color: var(--#{$prefix}info);
        border: 1px solid var(--#{$prefix}info) !important;
    }
}
</style>

<style lang="scss">
[data-bs-theme='dark'] .vue-notification {
    &.success {
        background: var(--#{$prefix}success);
        border-inline-start-color: darken($success-dark, 10%);
    }

    &.warning {
        background: var(--#{$prefix}warning);
        border-inline-start-color: darken($warning-dark, 10%);
    }

    &.info {
        background: var(--#{$prefix}info);
        border-inline-start-color: darken($info-dark, 10%);
    }
}
</style>
